import type { SmRole } from "@/domain/sm-models";
import type { BusinessProfile } from "@/lib/market-catalog";
import {
  inventoryNavLabel,
  insumosNavLabel,
  resolveStockControlMode,
} from "@/lib/stock-model";
import {
  defaultOperationalRoleForStaff,
  fmOperationalAccess,
  navPathAllowedByOperationalAccess,
} from "@/lib/fm-operational-runtime";
import type { ResolvedOperationalAccess } from "@structura/core/operational";
import type { CapabilityGateState } from "@structura/core/capability-gate";
import { resolveNavPlanGate } from "@/lib/capability-registry";
import type { SmPlanLimits } from "@/lib/plan-limits";
import {
  Activity,
  AlertCircle,
  BarChart3,
  Camera,
  ClipboardList,
  CreditCard,
  Download,
  Factory,
  GitBranch,
  LayoutDashboard,
  Brain,
  Sparkles,
  Store,
  Ticket,
  BadgePercent,
  Truck,
  Users,
  UtensilsCrossed,
  type LucideIcon,
  Package,
  Palette,
  Settings,
  Shield,
  Server,
  HeartPulse,
  Clock,
  ChefHat,
  HardDrive,
  Code2,
  MapPinned,
  CalendarCheck,
  FileText,
  Key,
  Monitor,
  Scale,
  RotateCcw,
  Briefcase,
  CalendarDays,
  ListOrdered,
  Star,
  Wallet,
  GraduationCap,
  FileSpreadsheet,
} from "lucide-react";

/**
 * `planFeatureAny`: soft gate — el ítem sigue visible con lock si el plan no lo incluye.
 * `onlyProfiles`: hard filter — el ítem se OCULTA si el businessProfile no está en la lista.
 *   Usarlo para módulos que no tienen sentido para ciertos rubros (ej: Salón para una dietética).
 *   Si no se define, el ítem se muestra para todos los perfiles.
 */
export interface WorkspaceNavItem {
  to: string;
  icon: LucideIcon;
  label: string;
  /** Texto corto para hubs (mobile nav / cards). La Sidebar desktop no lo muestra. */
  description?: string;
  /** Keys de SmPlanLimits y/o ids de SM_FEATURES (shipping_*, media, …). */
  planFeatureAny?: Array<keyof SmPlanLimits | string>;
  onlyProfiles?: BusinessProfile[];
}

export type WorkspaceNavItemResolved = WorkspaceNavItem & {
  gateState: CapabilityGateState;
  minPlanLabel?: string;
};

// Perfiles gastronómicos — mesas, insumos/BOM, comanda
const GASTRO: BusinessProfile[] = ["restaurant", "fast_food", "cafe"];
/** Nicho turnos (SCRUM-266). Ítems con este allowlist no aparecen en gastro/retail. */
const SERVICES: BusinessProfile[] = ["servicios"];
/**
 * Ítems compartidos que un tenant servicios SÍ ve (sin onlyProfiles).
 * Todo lo demás sin onlyProfiles (POS, catálogo, pesaje, etc.) se oculta —
 * el allowlist estático no se auto-oculta, hay que filtrar por perfil.
 */
export const SERVICES_NAV_SHARED = new Set([
  "/app/dashboard",
  "/app/metricas",
  "/app/clientes",
  "/app/pagos",
  "/app/settings",
  "/app/experience",
  "/app/renata",
  "/app/cumplimiento/solicitudes",
  "/app/cumplimiento/privacidad",
]);

/**
 * Muchos negocios de servicios (estética, peluquería) además venden productos.
 * Estas pantallas se suman al allowlist cuando el tenant tiene la tienda prendida:
 * sin ellas no hay forma de cargar productos desde el menú.
 */
export const SERVICES_NAV_SHOP = new Set(["/app/catalogacion", "/app/pedidos"]);

/** La tienda de un tenant servicios se define por sus flags, igual que el resto del panel. */
export function servicesTenantSellsProducts(
  productFlags?: Record<string, boolean> | null,
): boolean {
  return productFlags?.catalog_public === true || productFlags?.orders_online === true;
}

export function navItemVisibleForServicesProfile(
  item: WorkspaceNavItem,
  opts: { sellsProducts?: boolean } = {},
): boolean {
  if (item.onlyProfiles?.includes("servicios")) return true;
  if (item.onlyProfiles) return false;
  if (SERVICES_NAV_SHARED.has(item.to)) return true;
  return opts.sellsProducts === true && SERVICES_NAV_SHOP.has(item.to);
}
// Perfiles de compra física — supply chain (todos excepto digitales puros)
const PHYSICAL: BusinessProfile[] = [
  "restaurant",
  "fast_food",
  "cafe",
  "market",
  "dietetics",
  "pharmacy",
  "fashion",
  "bazar",
  "pet",
  "catalog",
];

export const workspaceNavItems: WorkspaceNavItem[] = [
  { to: "/app/dashboard", icon: LayoutDashboard, label: "Inicio", description: "Resumen del negocio" },
  // SCRUM-252: Métricas es un hub (Analytics Structura + GA4, extensible a futuros proveedores)
  // en vez de un acceso directo — ver getMetricasHubLinks() en src/lib/workspace-hubs.ts.
  { to: "/app/metricas", icon: BarChart3, label: "Métricas", description: "Indicadores y analíticas del negocio" },
  { to: "/app/pedidos", icon: ClipboardList, label: "Ventas", description: "POS y venta online" },
  { to: "/app/pesaje", icon: Scale, label: "Pesaje", description: "Ajuste de pesables", onlyProfiles: ["restaurant", "fast_food", "market", "dietetics"] },
  { to: "/app/ordenes/devoluciones", icon: RotateCcw, label: "Devoluciones", description: "Desde venta entregada" },
  // Abastecimiento = compras a proveedores (separado de ventas a clientes)
  {
    to: "/app/operaciones",
    icon: Truck,
    label: "Abastecimiento",
    description: "Compras a proveedores",
    planFeatureAny: ["supply_chain"],
    onlyProfiles: PHYSICAL,
  },
  { to: "/app/catalogacion", icon: Package, label: "Catalogación", description: "Productos y catálogo", planFeatureAny: ["catalog_public"] },
  { to: "/app/media", icon: Camera, label: "Biblioteca", description: "Imágenes y media", planFeatureAny: ["media"] },
  {
    to: "/app/digital",
    icon: Download,
    label: "Digitales",
    description: "Productos descargables",
    planFeatureAny: ["digital_products"],
    onlyProfiles: ["digital_products", "catalog"],
  },
  {
    to: "/app/eventos",
    icon: Ticket,
    label: "Eventos",
    description: "Entradas y fechas",
    onlyProfiles: ["fast_food", "restaurant", "market", "bazar"],
  },
  { to: "/app/clientes", icon: Users, label: "Clientes", description: "Personas y CRM" },
  {
    to: "/app/descuentos",
    icon: BadgePercent,
    label: "Incentivos de venta",
    description: "Cupones y promociones",
    planFeatureAny: ["discount_coupons", "promotions_engine", "combos", "composable_flows"],
  },
  { to: "/app/produccion", icon: Factory, label: "B2B / Mayorista", description: "Canales mayoristas", planFeatureAny: ["produccion_b2b"] },
  { to: "/app/pagos", icon: CreditCard, label: "Pagos", description: "Medios de cobro" },
  { to: "/app/facturacion", icon: FileText, label: "Facturación", description: "Comprobantes fiscales" },
  { to: "/app/reportes", icon: FileSpreadsheet, label: "Reportes", description: "Descargas XLS de ventas y stock", planFeatureAny: ["exports_csv"] },
  {
    to: "/app/logistica",
    icon: MapPinned,
    label: "Logística",
    description: "Envíos y entrega",
    planFeatureAny: [
      "shipping_shipnow",
      "shipping_tracking",
      "delivery_zones",
      "treggo_shipping",
      "andreani_shipping",
      "cabify_shipping",
      "uber_direct_shipping",
      "delivery_provider_pedidosya",
      "delivery_provider_rapiboy",
      "delivery_provider_shipnow",
      "zipnova_shipping",
      "pickit_shipping",
      "oca_epak_shipping",
      "correo_argentino_shipping",
    ],
    // Delivery / parcel — ocultar en niches 100% digitales
    onlyProfiles: PHYSICAL,
  },
  { to: "/app/pos", icon: Store, label: "POS / Caja", description: "Cobro en el local", planFeatureAny: ["payment_pos_mode"] },
  // Salón y Recetas — solo gastronomía
  {
    to: "/app/salon",
    icon: UtensilsCrossed,
    label: "Salón / Mesas",
    description: "Piso y comanda",
    planFeatureAny: ["dine_in_tables"],
    onlyProfiles: GASTRO,
  },
  {
    to: "/app/reservas",
    icon: CalendarCheck,
    label: "Reservas",
    description: "Turnos y mesas",
    planFeatureAny: ["table_reservations"],
    onlyProfiles: GASTRO,
  },
  {
    to: "/app/kds",
    icon: ChefHat,
    label: "Cocina (KDS)",
    description: "Pantalla de preparación",
    planFeatureAny: ["dine_in_tables"],
    onlyProfiles: GASTRO,
  },
  { to: "/app/experience", icon: Palette, label: "Configuración de la tienda", description: "Tienda pública", planFeatureAny: ["storefront_experience", "custom_branding"] },
  // Hardware Integration Platform — hard-gate por product flag (ver navItemsForRole)
  { to: "/app/hardware", icon: HardDrive, label: "Hardware", description: "Dispositivos del local" },
  // Digital Signage — hard-gate por product flag digital_signage (SCRUM-130)
  { to: "/app/pantallas", icon: Monitor, label: "Pantallas", description: "Menu boards y signage", planFeatureAny: ["digital_signage"] },
  { to: "/app/renata", icon: Sparkles, label: "Renata", description: "Asistente de IA", planFeatureAny: ["ai_assistant"] },
  // Nicho servicios (SCRUM-266) — rutas las llenan 267/268.
  { to: "/app/agenda", icon: CalendarDays, label: "Agenda", description: "Calendario de turnos", onlyProfiles: SERVICES },
  // "Turnos" y no "Reservas": en gastro /app/reservas ya son mesas, y el nombre
  // repetido chocaba con el título de su propio grupo en el sidebar.
  { to: "/app/turnos", icon: CalendarCheck, label: "Turnos", description: "Lista y detalle de turnos", onlyProfiles: SERVICES },
  { to: "/app/profesionales", icon: Users, label: "Profesionales", description: "Equipo que atiende", onlyProfiles: SERVICES },
  { to: "/app/servicios", icon: Briefcase, label: "Servicios", description: "Catálogo de prestaciones", onlyProfiles: SERVICES },
  { to: "/app/clases", icon: GraduationCap, label: "Clases", description: "Sesiones grupales, cupos y check-in", onlyProfiles: SERVICES },
  { to: "/app/horarios", icon: Clock, label: "Horarios", description: "Disponibilidad semanal", onlyProfiles: SERVICES },
  { to: "/app/packs", icon: Package, label: "Packs", description: "Bonos de sesiones", onlyProfiles: SERVICES },
  { to: "/app/lista-espera", icon: ListOrdered, label: "Lista de espera", description: "Cola y reclamos", onlyProfiles: SERVICES },
  { to: "/app/formularios", icon: FileText, label: "Formularios", description: "Intake previo al turno", onlyProfiles: SERVICES },
  { to: "/app/resenas", icon: Star, label: "Reseñas", description: "Opiniones post-turno", onlyProfiles: SERVICES },
  { to: "/app/liquidaciones", icon: Wallet, label: "Liquidaciones", description: "Comisiones a profesionales", onlyProfiles: SERVICES },
  { to: "/app/actividad-cobros", icon: Activity, label: "Actividad de cobros", description: "Webhooks de pago", onlyProfiles: SERVICES },
  { to: "/app/recursos", icon: FileText, label: "Recursos", description: "PDFs y rutinas para clientes", onlyProfiles: SERVICES },
  { to: "/app/metricas-servicios", icon: BarChart3, label: "Ocupación", description: "Ocupación y no-show", onlyProfiles: SERVICES },
  { to: "/app/cumplimiento/solicitudes", icon: Shield, label: "Cumplimiento", description: "Arrepentimiento y baja" },
  { to: "/app/cumplimiento/privacidad", icon: Shield, label: "Privacidad (DSAR)", description: "Exportar / eliminar datos" },
  { to: "/app/settings", icon: Settings, label: "Configuración", description: "Operación y cuenta" },
];

/** Anota items con estado de gate — NO oculta módulos bloqueados (product rule). */
export function annotateNavByPlan(
  items: WorkspaceNavItem[],
  planId: string | null | undefined,
  productFlags?: Record<string, boolean> | null,
): WorkspaceNavItemResolved[] {
  return items.map((item) => {
    const gate = resolveNavPlanGate(planId, item.planFeatureAny, productFlags);
    return { ...item, gateState: gate.state, minPlanLabel: gate.minPlanLabel };
  });
}

const staffPaths = new Set([
  "/app/dashboard",
  "/app/ordenes",
  "/app/operaciones",
  "/app/pedidos",
  "/app/pesaje",
  "/app/catalogacion",
  "/app/productos",
  "/app/categorias",
  "/app/guias-de-talles",
  "/app/combos",
  "/app/inventario",
  "/app/transferencias",
  "/app/clientes",
  "/app/produccion",
  "/app/metrics",
  "/app/metricas",
  "/app/reportes",
  "/app/analytics-ga",
  "/app/sucursales",
  "/app/settings",
  "/app/logistica",
  "/app/reservas",
  "/app/eventos",
  "/app/experience",
  "/app/cumplimiento",
  "/app/cumplimiento/solicitudes",
  "/app/cumplimiento/privacidad",
  "/app/pantallas",
]);

/** Rutas /admin/ops/* — observabilidad global del SaaS (paridad Booking Hub). */
export const platformOpsNavItems: { to: string; icon: LucideIcon; label: string }[] = [
  { to: "/admin/ops/health", icon: HeartPulse, label: "Salud de plataforma" },
  { to: "/admin/ops/infra", icon: Server, label: "Infraestructura" },
  { to: "/admin/ops/crons", icon: Clock, label: "Historial de tareas" },
  { to: "/admin/ops/backup", icon: HardDrive, label: "Copias de seguridad" },
  { to: "/admin/ops/logistics", icon: Truck, label: "Logística" },
  { to: "/admin/ops/events", icon: Activity, label: "Eventos" },
  { to: "/admin/ops/errors", icon: AlertCircle, label: "Errores" },
  { to: "/admin/ops/traces", icon: GitBranch, label: "Trazas" },
  { to: "/admin/ops/audit-log", icon: Shield, label: "Auditoría" },
  { to: "/admin/ops/snapshots", icon: Camera, label: "Snapshots" },
  { to: "/admin/ops/export", icon: Download, label: "Exportar datos" },
  { to: "/admin/ops/api-docs", icon: Code2, label: "Documentación API" },
  { to: "/admin/ops/renata", icon: Sparkles, label: "Renata" },
  { to: "/admin/ops/ai/providers", icon: Key, label: "Proveedores de IA" },
  { to: "/admin/ops/ai/health", icon: HeartPulse, label: "Salud de IA" },
  { to: "/admin/ops/ai/dashboard", icon: LayoutDashboard, label: "Panel de IA" },
  { to: "/admin/ops/ai/memory", icon: Brain, label: "Memoria de IA" },
];

export interface FmNavContext {
  businessProfile?: BusinessProfile | string;
  /** Rol operativo (cocina, caja, etc.) — distinto de SmRole técnico. */
  operationalRoleId?: string;
  lockedCapabilities?: string[];
  /** Flags de producto del tenant (inventory_bom, supply_chain, …). */
  productFlags?: Record<string, boolean>;
  /**
   * Matriz DB path→enabled para este perfil.
   * - path explícito enabled=false → oculta
   * - path explícito enabled=true → muestra (pasa onlyProfiles)
   * - path ausente → fallback a onlyProfiles estático
   */
  navPathFlags?: Map<string, boolean>;
  /**
   * SCRUM-289 (épica SCRUM-279 "Nichos dinámicos") — módulos de capacidad
   * activos del tenant. Solo tiene efecto si moduleIds.length > 1 (tenant
   * genuinamente híbrido) — con 0 o 1 módulo, navItemsForRole sigue el
   * camino legacy (onlyProfiles/profile escalar) sin ningún cambio, tal
   * como probó tests/unit/scrum284-nav-modules.test.ts.
   */
  moduleIds?: string[];
  /** Rutas que desbloquea cada módulo — catálogo de sm_capability_modules.navPaths. */
  moduleNavPaths?: Record<string, string[]>;
}


/**
 * Staff workspace nav visibility — single SoT: operational access.
 * Do NOT apply STAFF_PERMISSION_DEFAULTS here (admin/platform UI defaults only;
 * backend assertSmStaffPermission remains authorization law).
 */
export function filterStaffNavByOperationalAccess<T extends { to: string }>(
  items: T[],
  opAccess: ResolvedOperationalAccess | null,
  fallbackAllowlist: ReadonlySet<string> = staffPaths,
): T[] {
  return items.filter((item) => {
    if (opAccess) return navPathAllowedByOperationalAccess(opAccess, item.to);
    return fallbackAllowlist.has(item.to);
  });
}

/**
 * SCRUM-284/289 (épica SCRUM-279 "Nichos dinámicos", ADR-009) — filtro de
 * nav contra un SET de módulos activos en vez de un `businessProfile`
 * escalar. Cutover real hecho en SCRUM-289: `navItemsForRole` usa esta
 * función SOLO cuando el tenant tiene 2+ módulos activos (genuinamente
 * híbrido) — con 0 o 1 módulo sigue el camino legacy sin ningún cambio
 * (verificado en tests/unit/scrum284-nav-modules.test.ts para los 11
 * perfiles no-servicios; servicios puro sigue usando
 * `navItemVisibleForServicesProfile` — un tenant híbrido servicios+comercio
 * SÍ debe ver Ventas/Catalogación, a diferencia de un servicios puro, así
 * que la unión simple es el comportamiento correcto para el caso híbrido).
 *
 * Un tenant con 2+ módulos activos ve la UNIÓN de los nav_paths de todos
 * sus módulos — sin duplicar ítems (WorkspaceNavItem.to es único).
 */
export function navItemsForModules(
  items: WorkspaceNavItem[],
  moduleIds: string[],
  moduleNavPaths: Record<string, string[]>,
): WorkspaceNavItem[] {
  const allowedPaths = new Set<string>();
  for (const moduleId of moduleIds) {
    for (const path of moduleNavPaths[moduleId] ?? []) allowedPaths.add(path);
  }
  return items.filter((item) => {
    if (!item.onlyProfiles) return true;
    return allowedPaths.has(item.to);
  });
}

export function navItemsForRole(
  role: SmRole | undefined,
  planId?: string | null,
  ctx?: FmNavContext,
): WorkspaceNavItemResolved[] {
  // Filtrar por perfil de negocio antes de anotar el plan
  const profile = ctx?.businessProfile as BusinessProfile | undefined;
  // SCRUM-289: cutover real — solo activa la resolución por módulos cuando
  // el tenant tiene genuinamente 2+ módulos activos. Con 0 o 1, moduleIds
  // no altera nada y el camino de abajo es idéntico al de siempre.
  const isHybridTenant = (ctx?.moduleIds?.length ?? 0) > 1;
  const hybridAllowedItems = isHybridTenant
    ? new Set(navItemsForModules(workspaceNavItems, ctx!.moduleIds!, ctx?.moduleNavPaths ?? {}).map((i) => i.to))
    : null;
  const profileFiltered = workspaceNavItems.filter((item) => {
    const flags = ctx?.navPathFlags;
    if (flags?.has(item.to)) {
      return flags.get(item.to) === true;
    }
    if (hybridAllowedItems) {
      return hybridAllowedItems.has(item.to);
    }
    if (profile === "servicios") {
      return navItemVisibleForServicesProfile(item, {
        sellsProducts: servicesTenantSellsProducts(ctx?.productFlags),
      });
    }
    if (!item.onlyProfiles) return true;
    if (!profile) return true; // sin perfil → mostrar todo (fallback seguro)
    return item.onlyProfiles.includes(profile);
  });
  const stockMode = resolveStockControlMode(profile, {
    inventoryBom: ctx?.productFlags?.inventory_bom === true,
  });
  const planAnnotated = annotateNavByPlan(profileFiltered, planId, ctx?.productFlags)
    .filter((item) => {
      // Insumos solo tiene sentido con control BOM (ingredient/hybrid)
      if (item.to === "/app/ingredientes" && stockMode === "sku") return false;
      // Facturación electrónica: hard-gate por product flag
      if (
        item.to === "/app/facturacion"
        && ctx?.productFlags?.invoicing_tusfacturas !== true
        && ctx?.productFlags?.invoicing_afip_direct !== true
      ) {
        return false;
      }
      // Hardware Integration Platform: hard-gate por product flag (Pro+)
      if (item.to === "/app/hardware" && ctx?.productFlags?.hardware !== true) {
        return false;
      }
      return true;
    })
    .map((item) => {
      if (item.to === "/app/inventario") {
        return { ...item, label: inventoryNavLabel(stockMode) };
      }
      if (item.to === "/app/ingredientes") {
        return { ...item, label: insumosNavLabel(profile) };
      }
      return item;
    });

  const operationalRoleId =
    ctx?.operationalRoleId ??
    (role === "owner" || role === "super_admin"
      ? "owner"
      : role === "staff" && ctx?.businessProfile
        ? defaultOperationalRoleForStaff(ctx.businessProfile)
        : undefined);

  const opAccess =
    operationalRoleId && ctx?.businessProfile
      ? fmOperationalAccess(ctx.businessProfile, operationalRoleId, planId, ctx.lockedCapabilities)
      : null;

  if (role === "staff") {
    return filterStaffNavByOperationalAccess(planAnnotated, opAccess);
  }

  if (opAccess && role !== "super_admin") {
    return planAnnotated.filter((item) => navPathAllowedByOperationalAccess(opAccess, item.to));
  }

  return planAnnotated;
}
